import React, { useEffect, useMemo, useRef, useState } from 'react';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import BadgeReingreso from './BadgeReingreso';
import { listarRango, listarVuelvenDeAnses } from '../../services/turneroService';
import { buscarClientes } from '../../services/clienteService';
import { estiloEstado } from '../../constants/estadosTurno';
import { formatearTipoTramite } from '../../constants/tiposTramiteTurno';
import { apellidoNombre } from '../../utils/nombrePersona';
import { coincideBusqueda } from '../../utils/textoComparable';
import styles from '../../style/pages/Turnero.module.css';

const ESTADOS_REINGRESO_ABIERTO = new Set(['ASISTIO', 'EN_CURSO', 'EN_ANSES']);
// Por debajo de esto el padrón devuelve demasiado y no ayuda a elegir.
const MIN_CARACTERES_PADRON = 2;
const MAX_CLIENTES_PADRON = 8;

function fechaISOHoy() {
    const hoy = new Date();
    return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
}

function soloDigitos(valor) {
    return String(valor || '').replace(/\D/g, '');
}

/**
 * Busca al cliente que está en el mostrador para registrar su retorno. Arriba van los turnos
 * FINALIZADOS de hoy; si se pasa `onElegirCliente`, abajo se busca además en todo el padrón de
 * clientes, para quien no fue atendido hoy. Qué hacer con cada elección lo decide el padre.
 *
 * `onElegirCliente(cliente)` puede ser async: si rechaza con un Error, su mensaje se muestra acá
 * (ej.: el cliente no tiene ninguna atención de la que volver).
 *
 * Va dentro del overlay del turnero (styles.overlay + styles.contenidoModal). `accionExtra`
 * ({ texto, icono, onClick }) agrega una salida al pie para quien tampoco está en el padrón.
 */
function ModalBuscarAtendidoHoy({
    onCerrar,
    onElegir,
    onElegirCliente = null,
    titulo = 'Volvió cliente',
    subtitulo = 'Elegí a quién se atendió hoy para registrar su retorno',
    textoAccion = 'Registrar retorno',
    accionExtra = null,
}) {
    const [turnos, setTurnos] = useState([]);
    // Los que salieron hacia ANSES por el plan de pago (de cualquier día): van arriba y aparte,
    // porque su retorno es otro (abrir el plan) y es lo que más se busca cuando vuelven.
    const [vuelvenDeAnses, setVuelvenDeAnses] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [texto, setTexto] = useState('');

    const [clientes, setClientes] = useState([]);
    const [buscandoClientes, setBuscandoClientes] = useState(false);
    const [procesandoCuil, setProcesandoCuil] = useState(null);
    const [errorCliente, setErrorCliente] = useState('');
    const debounceRef = useRef(null);

    useEffect(() => {
        let activo = true;
        const hoyISO = fechaISOHoy();
        listarRango(hoyISO, hoyISO)
            .then(datos => { if (activo) setTurnos(Array.isArray(datos) ? datos : []); })
            .catch(e => {
                console.error('Error al cargar los atendidos de hoy:', e);
                if (activo) setError('No se pudieron cargar los clientes atendidos hoy.');
            })
            .finally(() => { if (activo) setCargando(false); });
        listarVuelvenDeAnses()
            .then(datos => { if (activo) setVuelvenDeAnses(Array.isArray(datos) ? datos : []); })
            .catch(() => { if (activo) setVuelvenDeAnses([]); });
        return () => { activo = false; };
    }, []);

    // Padrón completo, con el mismo debounce de 350ms que el resto de los buscadores de clientes.
    const buscaEnPadron = !!onElegirCliente;
    useEffect(() => {
        if (!buscaEnPadron) return undefined;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setErrorCliente('');
        const criterio = texto.trim();
        if (criterio.length < MIN_CARACTERES_PADRON) {
            setClientes([]);
            setBuscandoClientes(false);
            return undefined;
        }
        let activo = true;
        setBuscandoClientes(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const encontrados = await buscarClientes(criterio);
                if (activo) setClientes(Array.isArray(encontrados) ? encontrados : []);
            } catch (e) {
                if (activo) setClientes([]);
            } finally {
                if (activo) setBuscandoClientes(false);
            }
        }, 350);
        return () => { activo = false; clearTimeout(debounceRef.current); };
    }, [texto, buscaEnPadron]);

    // Los que ya tienen un reingreso en curso no se ofrecen: el backend lo rechazaría igual. Los
    // trámites exprés tampoco: no fueron una atención de la que se pueda "volver".
    const atendidos = useMemo(() => {
        const conReingresoAbierto = new Set(turnos
            .filter(t => t.esReingreso && ESTADOS_REINGRESO_ABIERTO.has(t.estado))
            .map(t => t.idTurnoOrigen));
        return turnos
            .filter(t => t.estado === 'FINALIZADO' && !t.esTramiteExpres && !conReingresoAbierto.has(t.idTurno))
            .sort((a, b) => String(b.finAtencion || '').localeCompare(String(a.finAtencion || '')));
    }, [turnos]);

    // Sin tildes y por palabras, igual que el resto de los buscadores de recepción.
    const filtrados = useMemo(() => atendidos.filter(t =>
        coincideBusqueda([t.apellido, t.nombre, t.dni, t.cuil, t.telefonoCliente, t.numeroIdentificacion], texto)),
    [atendidos, texto]);

    // Quien ya aparece entre los atendidos de hoy no se repite abajo: conviene elegirlo arriba,
    // que engancha el retorno a la atención de hoy.
    const vuelvenFiltrados = useMemo(() => vuelvenDeAnses.filter(t =>
        coincideBusqueda([t.apellido, t.nombre, t.dni, t.cuil, t.cuilCliente, t.telefonoCliente, t.numeroIdentificacion], texto)),
    [vuelvenDeAnses, texto]);

    const clientesPadron = useMemo(() => {
        const cuilsDeHoy = new Set(atendidos
            .flatMap(t => [soloDigitos(t.cuilCliente), soloDigitos(t.cuil)])
            .filter(Boolean));
        return clientes
            .filter(c => !cuilsDeHoy.has(soloDigitos(c.cuil)))
            .slice(0, MAX_CLIENTES_PADRON);
    }, [clientes, atendidos]);

    const elegirCliente = async (cliente) => {
        if (procesandoCuil) return;
        setErrorCliente('');
        setProcesandoCuil(cliente.cuil || '-');
        try {
            await onElegirCliente(cliente);
        } catch (e) {
            setErrorCliente(e?.message || 'No se pudo continuar con ese cliente.');
        } finally {
            setProcesandoCuil(null);
        }
    };

    const mostrarPadron = buscaEnPadron && texto.trim().length >= MIN_CARACTERES_PADRON;

    return (
        <>
            <div className={styles.modalHeader}>
                <div className={styles.modalHeaderIzq}>
                    <i className="bi bi-arrow-repeat" />
                    <div>
                        <h5 className={styles.modalTitulo}>{titulo}</h5>
                        <span className={styles.modalHeaderSubtitulo}>{subtitulo}</span>
                    </div>
                </div>
                <div className={styles.modalHeaderAcciones}>
                    <button type="button" className={styles.modalHeaderBtn} onClick={onCerrar}>
                        <i className="bi bi-x-lg" />
                    </button>
                </div>
            </div>

            <div className={styles.modalBody}>
                <div className={styles.filtrosAgenda}>
                    <div className={styles.filtrosFila}>
                        <div className={styles.filtroCampoAncho}>
                            <label className={styles.filtroLabel}>
                                <i className="bi bi-search me-2"></i>Buscar
                            </label>
                            <input
                                type="text"
                                className={`form-control ${styles.filtroInput}`}
                                placeholder={buscaEnPadron
                                    ? 'Nombre, apellido, DNI o CUIL del cliente…'
                                    : 'Nombre, DNI, CUIL, teléfono o número de turno…'}
                                value={texto}
                                onChange={(e) => setTexto(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </div>
                </div>

                {vuelvenFiltrados.length > 0 && (
                    <div className="mb-3">
                        <div className={styles.buscadorSeccionTitulo} style={{ color: '#047857' }}>
                            <i className="bi bi-cash-coin me-1" />Vuelven de ANSES por el plan de pago
                        </div>
                        <div className="d-flex flex-column gap-2">
                            {vuelvenFiltrados.map(t => (
                                <button
                                    key={`plan-${t.idTurno}`}
                                    type="button"
                                    className={`rec-item ${styles.filaAtendidoHoy}`}
                                    style={{ borderColor: '#6ee7b7', background: '#ecfdf5' }}
                                    onClick={() => onElegir(t)}
                                >
                                    <span className="rec-item-riel" style={{ background: '#10b981' }} />
                                    <div className={styles.filaAgendaHora}>
                                        <AccessTimeIcon fontSize="small" className="me-1" />
                                        {t.fechaTurno && t.fechaTurno !== fechaISOHoy()
                                            ? String(t.fechaTurno).split('-').reverse().slice(0, 2).join('/')
                                            : t.horario}
                                    </div>
                                    <div className={styles.filaAgendaCliente}>
                                        <div className={styles.filaAgendaNombre}>{apellidoNombre(t.apellido, t.nombre)}</div>
                                        <div className={styles.filaAgendaSub}>
                                            <span className="d-inline-flex align-items-center">
                                                <BadgeIcon fontSize="small" className="me-1" />
                                                {t.cuilCliente || t.cuil || t.dni || '-'}
                                            </span>
                                            <span className="ms-2">· Salió hacia ANSES: abre el plan</span>
                                        </div>
                                    </div>
                                    <span className={`rec-btn-primary ${styles.filaAtendidoHoyAccion}`}>
                                        <i className="bi bi-arrow-return-left me-1" />
                                        Volvió de ANSES
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {buscaEnPadron && <div className={styles.buscadorSeccionTitulo}>Atendidos hoy</div>}

                {cargando ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" />
                    </div>
                ) : error ? (
                    <p className="text-danger text-center py-4">{error}</p>
                ) : filtrados.length === 0 ? (
                    <div className={buscaEnPadron ? styles.buscadorVacioCompacto : styles.agendaVacia}>
                        {!buscaEnPadron && <i className="bi bi-person-x" style={{ fontSize: '3rem', color: '#cbd5e1' }} />}
                        <p style={{ color: '#94a3b8', marginTop: buscaEnPadron ? 0 : 12, marginBottom: 0 }}>
                            {atendidos.length === 0
                                ? 'Todavía no se finalizó ninguna atención hoy.'
                                : 'Ningún cliente atendido hoy coincide con la búsqueda.'}
                        </p>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        {filtrados.map(t => (
                            <button
                                key={t.idTurno}
                                type="button"
                                className={`rec-item ${styles.filaAtendidoHoy}`}
                                onClick={() => onElegir(t)}
                            >
                                <span className="rec-item-riel" style={{ background: estiloEstado(t.estado).riel }} />
                                <div className={styles.filaAgendaHora}>
                                    <AccessTimeIcon fontSize="small" className="me-1" />
                                    {t.horario}
                                </div>
                                <div className={styles.filaAgendaCliente}>
                                    <div className={styles.filaAgendaNombre}>
                                        {apellidoNombre(t.apellido, t.nombre)}
                                        <BadgeReingreso turno={t} className="ms-2" />
                                    </div>
                                    <div className={styles.filaAgendaSub}>
                                        <span className="d-inline-flex align-items-center">
                                            <BadgeIcon fontSize="small" className="me-1" />
                                            {t.dni || t.cuil || '-'}
                                        </span>
                                        <span className="ms-2">· {formatearTipoTramite(t.tipoTramite)}</span>
                                        {t.operadorNombre && <span className="ms-2">· Atendió {t.operadorNombre}</span>}
                                    </div>
                                </div>
                                <span className={`rec-btn-secondary ${styles.filaAtendidoHoyAccion}`}>
                                    <i className="bi bi-arrow-repeat me-1" />
                                    {textoAccion}
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {buscaEnPadron && (
                    <>
                        <div className={styles.buscadorSeccionTitulo}>Clientes del estudio</div>
                        {!mostrarPadron ? (
                            <div className={styles.buscadorVacioCompacto}>
                                <p className="mb-0" style={{ color: '#94a3b8' }}>
                                    Escribí al menos {MIN_CARACTERES_PADRON} letras para buscar en todos los clientes.
                                </p>
                            </div>
                        ) : buscandoClientes ? (
                            <div className={styles.buscadorVacioCompacto}>
                                <span className="spinner-border spinner-border-sm text-primary me-2" />
                                <span style={{ color: '#64748b' }}>Buscando clientes…</span>
                            </div>
                        ) : clientesPadron.length === 0 ? (
                            <div className={styles.buscadorVacioCompacto}>
                                <p className="mb-0" style={{ color: '#94a3b8' }}>
                                    {clientes.length === 0
                                        ? 'Ningún cliente del estudio coincide con la búsqueda.'
                                        : 'Los clientes que coinciden ya están entre los atendidos de hoy.'}
                                </p>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {clientesPadron.map(c => {
                                    const procesando = procesandoCuil === (c.cuil || '-');
                                    return (
                                        <button
                                            key={c.cuil || `${c.apellido}-${c.nombre}`}
                                            type="button"
                                            className={`rec-item ${styles.filaAtendidoHoy}`}
                                            onClick={() => elegirCliente(c)}
                                            disabled={!!procesandoCuil}
                                        >
                                            <span className="rec-item-riel" style={{ background: '#94a3b8' }} />
                                            <div className={styles.filaAgendaHora}>
                                                <i className="bi bi-person-vcard" style={{ fontSize: '1.3rem' }} />
                                            </div>
                                            <div className={styles.filaAgendaCliente}>
                                                <div className={styles.filaAgendaNombre}>
                                                    {apellidoNombre(c.apellido, c.nombre)}
                                                </div>
                                                <div className={styles.filaAgendaSub}>
                                                    <span className="d-inline-flex align-items-center">
                                                        <BadgeIcon fontSize="small" className="me-1" />
                                                        CUIL {c.cuil || '-'}
                                                    </span>
                                                    {c.telefonoPropio && <span className="ms-2">· {c.telefonoPropio}</span>}
                                                </div>
                                            </div>
                                            <span className={`rec-btn-secondary ${styles.filaAtendidoHoyAccion}`}>
                                                {procesando
                                                    ? <span className="spinner-border spinner-border-sm me-1" />
                                                    : <i className="bi bi-arrow-repeat me-1" />}
                                                {textoAccion}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {errorCliente && <div className="alert alert-warning py-2 mt-2 mb-0">{errorCliente}</div>}
                    </>
                )}

                {accionExtra && (
                    <div className={styles.filaAtendidoHoyPie}>
                        <span>¿No está en el sistema?</span>
                        <button type="button" className="rec-btn-primary" onClick={accionExtra.onClick}>
                            {accionExtra.icono && <i className={`bi ${accionExtra.icono} me-1`} />}
                            {accionExtra.texto}
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}

export default ModalBuscarAtendidoHoy;
